import { Client, ConsentState } from '@xmtp/browser-sdk';
import type { HorusSigner } from '../signers/types';
import { backupXmtpDatabase, restoreXmtpDatabase, ensureFirebaseBackup } from './xmtpBackup';

const XMTP_ENV = process.env.REACT_APP_NETWORK_TYPE === 'TESTNET' ? 'dev' : 'production';

/**
 * Custom error thrown when the XMTP installation limit (10/10) is reached.
 * Carries the inboxId (and optionally a client) so the UI can show a management modal.
 * When Client.create() itself fails, no client is available — only the inboxId
 * (extracted from the error message) and static SDK methods can be used.
 */
export class InstallationLimitError extends Error {
  client: Client | null;
  inboxId: string;
  constructor(inboxId: string, client: Client | null = null) {
    super('Installation limit reached');
    this.name = 'InstallationLimitError';
    this.inboxId = inboxId;
    this.client = client;
  }
}

/** Extract the InboxID from an XMTP installation limit error message. */
function extractInboxId(err: any): string {
  const match = err?.message?.match(/InboxID\s+([a-f0-9]+)/i);
  return match?.[1] || '';
}

export { XMTP_ENV };
const TAB_LOCK_KEY = 'horus-xmtp-tab-active';
const TAB_ID_KEY = 'horus-xmtp-tab-id';
const DB_KEY_PREFIX = 'horus-xmtp-dbkey-';

function getTabId(): string {
  let id = sessionStorage.getItem(TAB_ID_KEY);
  if (!id) {
    id = Math.random().toString(36).slice(2) + Date.now().toString(36);
    sessionStorage.setItem(TAB_ID_KEY, id);
  }
  return id;
}

/**
 * Get or create a persistent 32-byte encryption key for the XMTP local DB.
 * Stored in localStorage keyed by wallet address.
 */
function getExistingDbKey(address: string): Uint8Array | null {
  const storageKey = DB_KEY_PREFIX + address.toLowerCase();
  const existing = localStorage.getItem(storageKey);
  if (existing) {
    try {
      const arr = JSON.parse(existing);
      if (Array.isArray(arr) && arr.length === 32) {
        return new Uint8Array(arr);
      }
    } catch {
      // Corrupted
    }
  }
  return null;
}

function getOrCreateDbKey(address: string): Uint8Array {
  const existing = getExistingDbKey(address);
  if (existing) return existing;
  const key = crypto.getRandomValues(new Uint8Array(32));
  const storageKey = DB_KEY_PREFIX + address.toLowerCase();
  localStorage.setItem(storageKey, JSON.stringify(Array.from(key)));
  return key;
}

/**
 * Create a new XMTP client. Called on first-time setup or when no local DB exists.
 * This will prompt wallet signatures for identity registration.
 */
export async function createXmtpClient(
  signer: HorusSigner,
  address: string,
  onProgress?: (msg: string) => void
): Promise<Client> {
  onProgress?.('Creating encryption keys...');
  // If no existing key, clear any leftover OPFS data from old/corrupted sessions
  if (!getExistingDbKey(address)) {
    await clearXmtpStorage(address);
  }
  const dbEncryptionKey = getOrCreateDbKey(address);

  let client: Client;
  try {
    // Disable auto-register so we can handle the 10/10 installations limit ourselves.
    client = await Client.create(signer, {
      env: XMTP_ENV as any,
      dbEncryptionKey,
      disableAutoRegister: true,
    });
  } catch (err: any) {
    // The WASM layer may throw the 10/10 error during Client.create() itself.
    // Extract the inboxId from the error message and optionally build a cached client.
    if (isInstallationLimitMsg(err)) {
      const inboxId = extractInboxId(err);
      const builtClient = await buildXmtpClient(address).catch(() => null);
      throw new InstallationLimitError(inboxId, builtClient);
    }
    throw err;
  }

  // Register the identity on the XMTP network
  if (!(await client.isRegistered())) {
    onProgress?.('Registering identity on XMTP network...');
    try {
      await client.register();
    } catch (err: any) {
      // XMTP limits each identity to 10 installations (devices/browsers).
      // If the limit is reached, surface the error so the UI can show
      // a management modal for the user to choose which installations to revoke.
      if (isInstallationLimitMsg(err)) {
        const inboxId = extractInboxId(err) || client.inboxId || '';
        throw new InstallationLimitError(inboxId, client);
      } else {
        throw err;
      }
    }
  }

  // Sync conversations from the network immediately after creating a fresh client.
  // This is critical for new devices — ensures existing conversations are pulled
  // into the local DB before the first conversations.list() call.
  // Use aggressive sync with multiple rounds since the network needs time to
  // propagate the new installation to existing MLS groups.
  await syncConversationsAggressively(client, onProgress);

  // First backup: persist the OPFS database to Firebase Storage
  // so it can be restored on a new device or after key rotation.
  // Retry up to 3 times — this backup is critical to avoid creating
  // new installations on every device (10/10 limit).
  onProgress?.('Backing up encryption keys...');
  for (let attempt = 1; attempt <= 3; attempt++) {
    try {
      await backupXmtpDatabase(address, signer);
      console.log('[XMTP] Initial backup succeeded on attempt', attempt);
      break;
    } catch (err) {
      console.warn(`[XMTP] Initial backup attempt ${attempt}/3 failed:`, err);
      if (attempt < 3) await new Promise((r) => setTimeout(r, 2000));
    }
  }

  return client;
}

/** Check if an error message matches the XMTP 10/10 installation limit pattern. */
function isInstallationLimitMsg(err: any): boolean {
  const msg = err?.message || '';
  return msg.includes('10/10') || (msg.includes('installations') && msg.includes('register'));
}

/**
 * Complete registration after the user has revoked installations via the modal.
 * Retries register() with propagation delays, then aggressively syncs conversations.
 */
export async function registerAfterRevocation(
  client: Client,
  onProgress?: (msg: string) => void,
  signer?: HorusSigner,
  address?: string,
): Promise<Client> {
  onProgress?.('Waiting for network update...');
  await new Promise((r) => setTimeout(r, 3000));

  for (let attempt = 1; attempt <= 3; attempt++) {
    try {
      onProgress?.('Registering identity on XMTP network...');
      await client.register();
      break;
    } catch (retryErr: any) {
      console.warn(`Registration retry ${attempt}/3 failed:`, retryErr.message);
      if (attempt < 3) {
        await new Promise((r) => setTimeout(r, 3000));
      } else {
        throw retryErr;
      }
    }
  }

  // After re-registration the network needs time to propagate the new installation
  // to existing MLS groups. Use multiple sync rounds with delays.
  await syncConversationsAggressively(client, onProgress);

  // Backup after re-registration — retry to ensure Firebase has the new installation
  if (signer && address) {
    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        await backupXmtpDatabase(address, signer);
        console.log('[XMTP] Post-revocation backup succeeded on attempt', attempt);
        break;
      } catch (err) {
        console.warn(`[XMTP] Post-revocation backup attempt ${attempt}/3 failed:`, err);
        if (attempt < 3) await new Promise((r) => setTimeout(r, 2000));
      }
    }
  }

  return client;
}

/**
 * Aggressively sync conversations with multiple rounds.
 * After a new installation is registered, existing conversations may not be
 * immediately available because the network needs to propagate welcome messages.
 * This does multiple sync() + syncAll() rounds with delays to maximise retrieval.
 */
async function syncConversationsAggressively(
  client: Client,
  onProgress?: (msg: string) => void
): Promise<number> {
  let prevCount = 0;

  for (let round = 1; round <= 3; round++) {
    onProgress?.(`Syncing conversations from network (${round}/3)...`);
    // sync() refreshes known conversations but does NOT pull welcome messages.
    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        await client.conversations.sync();
        break;
      } catch (err) {
        console.warn(`Conversation sync round ${round} attempt ${attempt}/3 failed:`, err);
        if (attempt < 3) await new Promise((r) => setTimeout(r, 1000));
      }
    }
    // syncAll() processes MLS welcome messages to discover NEW conversations
    // from other users. Must include Unknown consent to find new DMs.
    try {
      await client.conversations.syncAll([ConsentState.Unknown, ConsentState.Allowed]);
    } catch (err) {
      console.warn(`syncAll round ${round} failed:`, err);
    }

    // Check if new conversations appeared — break early if count stabilised
    try {
      const convos = await client.conversations.list({
        consentStates: [ConsentState.Unknown, ConsentState.Allowed],
      });
      const count = convos.length;
      if (count > 0 && count === prevCount) {
        console.log(`[XMTP] Aggressive sync round ${round}: ${count} conversations (stable), stopping early`);
        return count;
      }
      prevCount = count;
      console.log(`[XMTP] Aggressive sync round ${round}: ${count} conversations found`);
    } catch { /* non-critical */ }

    // Shorter delays: 1s after round 1, 2s after round 2
    if (round < 3) {
      await new Promise((r) => setTimeout(r, round === 1 ? 1000 : 2000));
    }
  }
  return prevCount;
}

/**
 * Build (reconnect) an existing XMTP client from the local OPFS database.
 * No wallet signatures needed - uses cached identity.
 */
export async function buildXmtpClient(
  address: string,
  onProgress?: (msg: string) => void
): Promise<Client | null> {
  // Only attempt build if we have a stored encryption key from a previous session.
  // If no key exists, the old DB was created without encryption and is incompatible.
  onProgress?.('Reconnecting from cached session...');
  const dbEncryptionKey = getExistingDbKey(address);
  if (!dbEncryptionKey) {
    console.warn('No stored XMTP DB key — skipping build, will create fresh client');
    await clearXmtpStorage(address);
    return null;
  }

  try {
    const identifier = {
      identifier: address.toLowerCase(),
      identifierKind: "Ethereum" as const,
    };
    const client = await Client.build(identifier, {
      env: XMTP_ENV as any,
      dbEncryptionKey,
    });
    return client;
  } catch (err) {
    console.warn('XMTP build failed, clearing corrupted OPFS:', err);
    // Only clear OPFS — preserve the localStorage key so Firebase restore
    // can still match it or overwrite it with the backed-up key.
    await clearXmtpStorage(address);
    return null;
  }
}

async function clearXmtpStorage(address: string): Promise<void> {
  try {
    const root = await navigator.storage?.getDirectory?.();
    if (root) {
      const addr = address.toLowerCase();
      for await (const [name] of (root as any).entries()) {
        // Only clear OPFS entries belonging to this specific wallet address
        if (name.includes(addr)) {
          await root.removeEntry(name, { recursive: true }).catch(() => {});
        }
      }
    }
  } catch {
    // OPFS cleanup not critical
  }
}

/**
 * Initialize XMTP client — local first for speed, then sync with Firebase:
 *   1. Try local data first (fast, no network needed)
 *      → On success, sync local state to Firebase in background
 *   2. If no local data → restore from Firebase backup → rebuild
 *      → If restored but not registered, try re-registration before creating new
 *   3. Only create a new identity as an absolute last resort
 *      → Always upload to Firebase so next device can restore
 *
 * This ensures all devices share the same installation identity,
 * minimizing unnecessary installation slot consumption (max 10 per inbox).
 */
export async function initializeXmtpClient(
  signer: HorusSigner,
  address: string,
  onProgress?: (msg: string) => void,
  options?: { allowCreateNew?: boolean }
): Promise<Client> {
  const allowCreateNew = options?.allowCreateNew !== false;
  console.log('[XMTP Init] initializeXmtpClient start', { address, allowCreateNew });
  // Request persistent storage to prevent mobile browsers from evicting OPFS data
  try {
    if (navigator.storage?.persist) {
      await navigator.storage.persist();
    }
  } catch { /* non-critical */ }

  // ---- Step 1: Try local data first (fastest path) ----
  const existingClient = await buildXmtpClient(address, onProgress);
  if (existingClient) {
    try {
      onProgress?.('Verifying registration...');
        if (await existingClient.isRegistered()) {
          console.log('[XMTP Init] Using local client, syncing to Firebase...');
        // Local works — always sync to Firebase so other devices can restore
        ensureFirebaseBackup(address, signer).catch((err) => {
          console.warn('[XMTP Init] Background Firebase sync failed:', err);
        });
        return existingClient;
      }
        console.warn('[XMTP Init] Local client built but not registered on network');
    } catch (err) {
      // Network/transient error — trust the local data
      console.warn('[XMTP Init] isRegistered() check failed (transient), using local client:', err);
      ensureFirebaseBackup(address, signer).catch(() => {});
      return existingClient;
    }
    try { existingClient.close(); } catch { /* ignore */ }
  }

  // ---- Step 2: No local data — try Firebase backup restore ----
  try {
    onProgress?.('Checking for message backup...');
    console.log('[XMTP Init] No local client, attempting Firebase restore...');
    const restored = await restoreXmtpDatabase(address, signer);
    console.log('[XMTP Init] Firebase restore result:', restored);
    if (restored) {
      onProgress?.('Restored messages from backup!');
      const restoredClient = await buildXmtpClient(address, onProgress);
      console.log('[XMTP Init] buildXmtpClient after restore:', !!restoredClient);
      if (restoredClient) {
        try {
          onProgress?.('Verifying registration...');
          if (await restoredClient.isRegistered()) {
            console.log('[XMTP Init] Using restored client from Firebase backup');
            await syncConversationsAggressively(restoredClient, onProgress);
            // Even if there are 0 conversations, the restored installation is valid.
            // Do not create a fresh installation, or we will burn slots (10/10 limit).
            return restoredClient;
          } else {
            // Restored installation not registered (may have been revoked).
            // Try to re-register the existing identity before creating a new one —
            // this avoids consuming a new installation slot.
            console.warn('[XMTP Init] Restored client not registered — attempting re-registration...');
            onProgress?.('Re-registering restored identity...');
            try {
              await restoredClient.register();
              console.log('[XMTP Init] Restored client re-registered successfully');
              await syncConversationsAggressively(restoredClient, onProgress);
              // Update Firebase with the re-registered state
              backupXmtpDatabase(address, signer).catch((bErr) =>
                console.warn('[XMTP Init] Backup after re-registration failed:', bErr)
              );
              // Even with 0 conversations, use the restored installation
              // to avoid consuming a new installation slot.
              return restoredClient;
            } catch (regErr: any) {
              console.warn('[XMTP Init] Re-registration failed:', regErr?.message);
              if (isInstallationLimitMsg(regErr)) {
                const inboxId = extractInboxId(regErr) || restoredClient.inboxId || '';
                throw new InstallationLimitError(inboxId, restoredClient);
              }
            }
          }
        } catch (err: any) {
          if (err?.name === 'InstallationLimitError') throw err;
          // Network/transient error — trust the restored data
          console.warn('[XMTP Init] isRegistered() failed (transient), using restored client:', err);
          return restoredClient;
        }
        try { restoredClient.close(); } catch { /* ignore */ }
      }
    }
  } catch (err: any) {
    if (err?.name === 'InstallationLimitError') throw err;
    console.warn('[XMTP Init] Firebase restore attempt failed:', err);
  }

  // ---- Step 3: Last resort — create new identity + registration ----
  if (!allowCreateNew) {
    console.warn('[XMTP Init] Skipping new identity creation (allowCreateNew=false)');
    throw new Error('No local or backup data. Skipping new XMTP identity creation.');
  }
  onProgress?.('Creating new XMTP identity...');
  return createXmtpClient(signer, address, onProgress);
}

/**
 * Close and cleanup XMTP client.
 */
export async function closeXmtpClient(client: Client | null): Promise<void> {
  if (!client) return;
  try {
    client.close();
    releaseTabLock();
  } catch (err) {
    console.error('Error closing XMTP client:', err);
  }
}

function parseLock(raw: string): { tabId: string | null; timestamp: number } {
  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed === 'object' && parsed !== null && typeof parsed.tabId === 'string') {
      return { tabId: parsed.tabId, timestamp: parsed.timestamp || 0 };
    }
  } catch {
    // not JSON
  }
  // Old format (plain number string) or corrupt — no tab ownership info
  const ts = parseInt(raw, 10);
  return { tabId: null, timestamp: isNaN(ts) ? 0 : ts };
}

/**
 * Check if another tab already has an active XMTP session.
 * OPFS SQLite doesn't support multi-tab access.
 */
export function acquireTabLock(): boolean {
  const myTabId = getTabId();

  // If this tab already owns the lock via interval, allow re-entry
  if ((window as any).__xmtpTabLockInterval) {
    console.log('[XMTP TabLock] Re-entry allowed (interval already running)', { myTabId });
    return true;
  }

  const existing = localStorage.getItem(TAB_LOCK_KEY);
  if (existing) {
    const lock = parseLock(existing);
    console.log('[XMTP TabLock] Existing lock found', { myTabId, lock, existing });
    // Old-format lock (no tabId) — always take it over since we can't
    // tell if it's ours or another tab's, and no interval is refreshing it
    if (lock.tabId !== null) {
      const isOwnLock = lock.tabId === myTabId;
      const isStale = Date.now() - lock.timestamp > 30000;
      // Block only if a different tab holds a fresh lock
      if (!isOwnLock && !isStale) {
        console.warn('[XMTP TabLock] Lock held by another tab (fresh)', {
          myTabId,
          lockTabId: lock.tabId,
          ageMs: Date.now() - lock.timestamp,
        });
        return false;
      }
    }
  }

  const lockValue = JSON.stringify({ tabId: myTabId, timestamp: Date.now() });
  localStorage.setItem(TAB_LOCK_KEY, lockValue);
  console.log('[XMTP TabLock] Lock acquired', { myTabId });
  // Keep refreshing the lock
  const interval = setInterval(() => {
    localStorage.setItem(TAB_LOCK_KEY, JSON.stringify({ tabId: myTabId, timestamp: Date.now() }));
  }, 10000);
  (window as any).__xmtpTabLockInterval = interval;

  // Clean up lock when tab is closed
  window.addEventListener('beforeunload', releaseTabLock);

  return true;
}

export function releaseTabLock(): void {
  localStorage.removeItem(TAB_LOCK_KEY);
  window.removeEventListener('beforeunload', releaseTabLock);
  const interval = (window as any).__xmtpTabLockInterval;
  if (interval) {
    clearInterval(interval);
    delete (window as any).__xmtpTabLockInterval;
  }
}

/**
 * Check if an address can receive XMTP messages (has registered on the network).
 */
export async function canMessage(
  client: Client,
  addresses: string[]
): Promise<Map<string, boolean>> {
  const result = new Map<string, boolean>();
  try {
    const identifiers = addresses.map((addr) => ({
      identifier: addr.toLowerCase(),
      identifierKind: "Ethereum" as const,
    }));
    const canMessageResult = await client.canMessage(identifiers);
    // SDK may key results by Identifier objects, not address strings.
    // Use positional matching from the values for robustness.
    const values = Array.from(canMessageResult.values());
    addresses.forEach((addr, i) => {
      result.set(addr.toLowerCase(), values[i] ?? false);
    });
  } catch (err) {
    console.error('Error checking canMessage:', err);
    addresses.forEach((addr) => result.set(addr.toLowerCase(), false));
  }
  return result;
}
